/**
 * `useApiAction` — fire a worker special-endpoint POST (retry, replay, disable,
 * callback) and invalidate the affected Refine resource caches.
 *
 * Implemented with the Refine API base URL + native `fetch` (credentials
 * included for the worker session cookie) rather than `useCustomMutation`,
 * because the placeholder dataProvider's `custom` method is not yet wired and
 * the D1 implementation shape is not pinned. This keeps D5 self-contained.
 */
import { useApiUrl, useInvalidate, useNotification } from "@refinedev/core";
import { useCallback, useState } from "react";

export interface ApiActionOptions {
  /** HTTP method, default POST. */
  method?: "POST" | "PATCH" | "PUT" | "DELETE";
  /** JSON body to send. */
  body?: unknown;
  /** Resources whose list/detail caches should be invalidated on success. */
  invalidateResources?: string[];
  /** Toast text on success. */
  successMessage?: string;
}

export interface ApiActionState {
  loading: boolean;
  error: string | null;
}

export function useApiAction(): ApiActionState & {
  run: (path: string, options?: ApiActionOptions) => Promise<unknown>;
} {
  const apiUrl = useApiUrl();
  const invalidate = useInvalidate();
  const notification = useNotification();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (path: string, options: ApiActionOptions = {}) => {
      const { method = "POST", body, invalidateResources, successMessage } =
        options;
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${apiUrl}${path}`, {
          method,
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: body === undefined ? undefined : JSON.stringify(body),
        });
        if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          try {
            const json = (await res.json()) as { error?: string };
            if (json?.error) detail = json.error;
          } catch {
            /* response had no JSON body */
          }
          throw new Error(detail);
        }
        const data = (await res.json().catch(() => null)) as unknown;

        if (invalidateResources?.length) {
          await Promise.all(
            invalidateResources.map((resource) =>
              invalidate({ resource, invalidates: ["list", "detail"] }),
            ),
          );
        }
        if (successMessage) {
          notification.open?.({
            type: "success",
            message: successMessage,
            key: `api-action-${path}`,
          });
        }
        return data;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        notification.open?.({
          type: "error",
          message: "Action failed",
          description: message,
          key: `api-action-error-${path}`,
        });
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, invalidate, notification],
  );

  return { loading, error, run };
}
